import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Heading3, Jumbo, tokens } from '@treams/treams-ui';

import SettingsCollection, { ArchiveRestoreMutationTuple } from '../generic/SettingsCollection';
import PredefinedTemplates from './configurations/PredefinedTemplates';

const ContentItemButton = styled(Box)`
	background-color: ${(props) => rgba(props.theme.colors.white, 0.2)};
	transition: 150ms background-color ease-out;
	border-radius: 20px;
	color: ${(props) => props.theme.colors.white};
`;

type ContentItemProps = { tint: 'soho' | 'sun' | 'tropical' };

const ContentItem = styled(Flex)<ContentItemProps>`
	cursor: pointer;
	border-radius: 6px;
	:hover {
		${ContentItemButton} {
			background-color: ${(props) => rgba(props.theme.colors.white, 0.3)};
		}
	}

	${(props) =>
		props.tint === 'soho' &&
		`
		background-image: linear-gradient(320deg, #FF1B6B, #45CAFF);
`}

	${(props) =>
		props.tint === 'sun' &&
		`
	background-image: linear-gradient(320deg, #FF930F, #FFF95B);
`}
${(props) =>
		props.tint === 'tropical' &&
		`
	background-image: linear-gradient(320deg, #F9B16E, #F68080);
`}
`;

export default function EvaluationFormsListContainer() {
	const history = useHistory();
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [duplicate] = graphql.useCreateEvaluationFormMutation();

	const onDuplicate = async (form: graphql.CreateEvaluationFormInput) => {
		const result = await duplicate({
			variables: {
				input: form,
			},
		});
		if (result.data) {
			history.push(`/settings/conversations/${result.data.createEvaluationForm.evaluationForm.id}`);
		}
	};

	return (
		<>
			<Jumbo
				title={t('settings.conversations.templates.list.title')}
				description={t('settings.conversations.templates.list.description')}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Heading3 mb={3}>{t('evaluationForms.list.templates.title')}</Heading3>
				<Flex justifyContent="flex-start" flexDirection="row" mb={3}>
					{PredefinedTemplates.map((template) => (
						<ContentItem
							width={3 / 12}
							mr={3}
							p={3}
							height={200}
							key={template.name}
							justifyContent="space-between"
							flexDirection="column"
							tint={template.tint as ContentItemProps['tint']}
							onClick={() => onDuplicate(template.form as graphql.CreateEvaluationFormInput)}
						>
							<Flex justifyContent="flex-end">
								<ContentItemButton px={3} py={2}>
									{t('evaluationForms.list.templates.useTemplate')}
								</ContentItemButton>
							</Flex>
							<Heading3 color="white">{localize(template.form.title).text}</Heading3>
						</ContentItem>
					))}
				</Flex>
				<SettingsCollection
					query={graphql.useEvaluationFormsQuery}
					lazyQuery={graphql.useEvaluationFormsLazyQuery}
					field="evaluationForms"
					translationKey="settings.evaluationForms"
					title={(form: graphql.EvaluationForm) => localize(form.title).text}
					link={(form) => `/settings/conversations/${form.id}`}
					variables={{}}
					archive={
						graphql.useArchiveEvaluationFormMutation() as unknown as ArchiveRestoreMutationTuple
					}
					restore={
						graphql.useRestoreEvaluationFormMutation() as unknown as ArchiveRestoreMutationTuple
					}
					onAdd={() => history.push('/settings/conversations/new')}
				/>
			</Container>
		</>
	);
}
